<script setup lang="ts">
type Orientation = "vertical" | "horizontal";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);

const dataStep = field<number>("dataStep", 1);
const orientation = field<Orientation>("orientation", "vertical");
const colorAccessor = field<string>("colorAccessor", "(d) => null");

const series = computed({
  get: () => ((block.value?.series ?? []) as string[]).join(", "),
  set: (value: string) => patch({ series: value.split(",").map((item) => item.trim()).filter(Boolean) }),
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:chart-column-big" accent="reporting">
    <EditorField :label="t('seriesLabel')" :hint="t('seriesHint')">
      <Input v-model="series" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="score, potential" />
    </EditorField>

    <EditorField :label="t('dataStepLabel')">
      <Input v-model.number="dataStep" type="number" min="1" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('orientationLabel')">
      <Select :model-value="orientation" @update:model-value="(value) => (orientation = value as Orientation)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="vertical">{{ t("vertical") }}</SelectItem>
          <SelectItem value="horizontal">{{ t("horizontal") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('colorAccessorLabel')" :hint="t('colorAccessorHint')">
      <Textarea v-model="colorAccessor" rows="7" class="rounded-sm bg-muted font-mono text-[12px]" />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Grouped Bar Chart"
  seriesLabel: "Series"
  seriesHint: "Comma-separated data keys. Leave blank to auto-detect numeric fields."
  dataStepLabel: "Data Step"
  orientationLabel: "Orientation"
  vertical: "Vertical"
  horizontal: "Horizontal"
  colorAccessorLabel: "Color Accessor"
  colorAccessorHint: "JavaScript function used by Unovis to resolve series colors."
</i18n>
